<script setup>
  import {computed} from "vue";

  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import PatientinputComponentStep from "@pages/Patientinput/Components/Step.vue";
  import {circumstancesCarer, circumstancesPatient} from "@pages/Patientinput/Enums.js";
  import {MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientinputComponentStepCircumstances",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  const circumstances = computed(() => {
    const currentCircumstances = props.form.meta.is_patient ? circumstancesPatient : circumstancesCarer;
    return currentCircumstances.filter((circumstance) => showCircumstanceForCurrentGender(circumstance));
  });

  const showCircumstanceForCurrentGender = (circumstance) => {
    // show all circumstances for diverse gender
    if (props.form.masterdata.patient.gender === "diverse") {
      return true;
    }

    if (Object.hasOwn(circumstance, "gender")) {
      if (typeof circumstance.gender === "string") {
        return circumstance.gender === props.form.masterdata.patient.gender;
      } else if (typeof circumstance.gender === "object") {
        return circumstance.gender.includes(props.form.masterdata.patient.gender);
      }
    }

    return true;
  };

  const attributeCircumstancesChanged = (attribute, selected) => {
    if (Object.hasOwn(attribute, "attributeOptions") && !selected) {
      attribute.attributeOptions.forEach((select) => {
        props.form.circumstances.attributes[attribute.key].payload[select.attribute] = null;
      });
    }
  };

  const mivSelected = (miv) => {
    return props.form.circumstances.mivs.filter((iMiv) => iMiv === miv).length === 1;
  };

  const mivCheckboxChanged = (miv, selected) => {
    props.form.circumstances.mivs = props.form.circumstances.mivs.filter((iMiv) => iMiv !== miv);

    if (selected) {
      props.form.circumstances.mivs.push(miv);
    }
  };
</script>

<template>
  <PatientinputComponentStep
    :title="form.meta.is_patient ? 'Lebensumstände' : 'Weitere Angaben'"
    :test-id="getTestId()"
  >
    <p>Bitte kreuzen Sie zutreffende Lebensumstände an.</p>

    <template v-for="(circumstance, index) in circumstances" :key="index">
      <!-- Patient Attribute with special config -->
      <template v-if="typeof form.circumstances.attributes[circumstance.key] === 'object'">
        <MedFormField orientation="horizontal" size="mobile">
          <MedCheckbox
            v-model="form.circumstances.attributes[circumstance.key].selected"
            variant="primary"
            :data-test="getTestId(`circumstances-step-${index}-checkbox`)"
            @change="(event) => attributeCircumstancesChanged(circumstance, event)"
          />

          <MedLabel>
            {{ circumstance.label }}
          </MedLabel>
        </MedFormField>

        <div
          v-if="
            Object.hasOwn(circumstance, 'attributeOptions') && form.circumstances.attributes[circumstance.key].selected
          "
          class="flex flex-col gap-4 pl-9"
        >
          <template v-for="(option, oIndex) in circumstance.attributeOptions" :key="oIndex">
            <ComponentSelect
              v-if="option.type === 'select'"
              v-model="form.circumstances.attributes[circumstance.key].payload[option.attribute]"
              :label="option.label"
              :options="option.options"
              large
              :test-id="getTestId(`circumstances-step-${index}-attribute-option-${oIndex}`)"
            />

            <ComponentTextarea
              v-else-if="option.type === 'textarea'"
              v-model="form.circumstances.attributes[circumstance.key].payload[option.attribute]"
              :label="option.label"
              large
              :test-id="getTestId(`circumstances-step-${index}-attribute-option-${oIndex}`)"
            />

            <ComponentInput
              v-else
              v-model="form.circumstances.attributes[circumstance.key].payload[option.attribute]"
              :type="option.type"
              :label="option.label"
              large
              autocomplete="off"
              :test-id="getTestId(`circumstances-step-${index}-attribute-option-${oIndex}`)"
            />
          </template>
        </div>
      </template>

      <!-- only MIV-Code -->
      <template v-else-if="circumstance.miv">
        <MedFormField orientation="horizontal">
          <MedCheckbox
            :id="`cb-${circumstance.testId}`"
            size="mobile"
            variant="primary"
            :model-value="mivSelected(circumstance.miv)"
            :data-test="getTestId(`circumstances-step-${index}-miv-checkbox`)"
            @change="(event) => mivCheckboxChanged(circumstance.miv, event)"
          />

          <MedLabel :for="`cb-${circumstance.testId}`" clickable class="text-base">
            {{ circumstance.label }}
          </MedLabel>
        </MedFormField>
      </template>

      <!-- Patient Attribute without special config -->
      <template v-else>
        <MedFormField orientation="horizontal" size="mobile">
          <MedCheckbox
            v-model="form.circumstances.attributes[circumstance.key]"
            variant="primary"
            :data-test="getTestId(`circumstances-step-${index}-else-checkbox`)"
          />

          <MedLabel>
            {{ circumstance.label }}
          </MedLabel>
        </MedFormField>
      </template>
    </template>
  </PatientinputComponentStep>
</template>
